import type { Filter, FilterField } from '@/components/filter/constants';
import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import type { RequestOptions } from '@/utils/request';
import type { SerializedCustomer, SerializedMeter } from '@enmon/commons/lib/enmon';
import type {
  SerializedEmissionFactor,
  SerializedEmissionFactorCategory,
  SerializedHardwareItem,
  SerializedManufacturer,
  SerializedMeterModel,
  SerializedThirdParty,
  ThirdPartyType,
} from '@enmon/commons/lib/tools';
import type { Locale } from '@enmon/commons/lib/shared';
import type { SettingsType } from '@enmon/commons/lib/shared/settings';
import type { BubbleSettings, GuideSettings } from '@enmon/commons/lib/enmon/settings/settings';

export type ToolsFilter = Pick<Filter, FilterField.EmissionFactorCategories | FilterField.Sorts>;
export interface SettingsFilter {
  locale: Locale;
}

// Argument types
export type GetEmissionCategoriesArg = ToolsFilter;
export type GetEmissionFactorsArg = ToolsFilter;
export type GetThirdPartiesArg = {
  customers: SerializedCustomer['_id'][];
  thirdPartyTypes: ThirdPartyType[];
};
export type GetSettingsArgs = SettingsFilter;

export type GetManufacturerArg = {
  populate?: 'meter_models';
};
export type GetOneManufacturerArg = {
  manufacturerId: SerializedMeter['manufacturer'];
  populate?: 'meter_models';
};

export type GetOneManufacturerResult =
  | (Pick<SerializedManufacturer, 'id' | 'name' | 'parent'> & {
      meter_models?: Array<Pick<SerializedMeterModel, 'id' | 'name' | 'commodity'>>;
    })
  | null;

export type GetManufacturersResult = Array<NonNullable<GetOneManufacturerResult>> | null;

export type GetHardwareItemsArg = {
  inventoryMeterId?: string | null;
  active?: boolean;
};

// Result types
export type GetEmissionCategoriesResult = SerializedEmissionFactorCategory[] | null;
export type GetEmissionFactorsResult = SerializedEmissionFactor[] | null;
export type GetThirdPartiesResult = SerializedThirdParty[] | null;
export type GetBubbleSettingsResult = BubbleSettings[] | null;
export type GetGuideSettingsResult = GuideSettings[] | null;
export type GetHardwareItemsResult = SerializedHardwareItem[] | null;

export class ToolsApiClient extends BaseApiClient {
  constructor() {
    super('tools');
  }

  public getEmissionCategories(
    filter: GetEmissionCategoriesArg,
    signal: RequestOptions['signal'],
  ): Promise<GetEmissionCategoriesResult> {
    return this.http.get('emission-factor-categories', {
      query: getQuery(filter),
      signal,
    });
  }

  public getEmissionFactors(
    filter: GetEmissionFactorsArg,
    signal: RequestOptions['signal'],
  ): Promise<GetEmissionFactorsResult> {
    return this.http.get('emission-factors', {
      query: getQuery(filter),
      signal,
    });
  }

  public getThirdParties(filter: GetThirdPartiesArg, signal: RequestOptions['signal']): Promise<GetThirdPartiesResult> {
    return this.http.get('third-parties', {
      query: getQuery(filter),
      signal,
    });
  }

  public getSettings(
    type: SettingsType.Bubble,
    locale: GetSettingsArgs['locale'],
    signal: RequestOptions['signal'],
  ): Promise<GetBubbleSettingsResult>;
  public getSettings(
    type: SettingsType.Guide,
    locale: GetSettingsArgs['locale'],
    signal: RequestOptions['signal'],
  ): Promise<GetGuideSettingsResult>;
  public getSettings(
    type: SettingsType,
    locale: GetSettingsArgs['locale'],
    signal: RequestOptions['signal'],
  ): Promise<GetBubbleSettingsResult | GetGuideSettingsResult> {
    return this.http.get(`settings/${type}`, { query: getQuery({ locale }), signal });
  }

  public getOneManufacturer(
    { manufacturerId, populate }: GetOneManufacturerArg,
    signal: RequestOptions['signal'],
  ): Promise<GetOneManufacturerResult> {
    if (!manufacturerId) {
      return Promise.resolve(null);
    }

    return this.http.get(`manufacturers/${manufacturerId}`, {
      signal,
      query: getQuery({ populate }),
    });
  }

  public getManufacturers(
    { populate }: GetManufacturerArg,
    signal: RequestOptions['signal'],
  ): Promise<GetManufacturersResult> {
    return this.http.get('manufacturers', { signal, query: getQuery({ populate }) });
  }

  public getHardwareItems(
    { inventoryMeterId, active }: GetHardwareItemsArg,
    signal: RequestOptions['signal'],
  ): Promise<GetHardwareItemsResult> {
    return this.http.get(`inventory-meters/${inventoryMeterId}/hardware-items`, {
      signal,
      query: getQuery({ active }),
    });
  }
}
